import * as Sentry from "@sentry/react";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { InputText, InputWrapper, Text } from "@performyard/py-components";
import { TEMPLATE_LIBRARY_PERMISSIONS } from "@performyard/service-contracts";
import type { TemplateLibraryEntryTypeEnum } from "@performyard/service-contracts";

import PageTitle from "../../components/PageTitle";
import { TemplateLibraryDetailPanel } from "./components/TemplateLibraryDetailPanel";
import { TemplateLibraryFilterPills } from "./components/TemplateLibraryFilterPills";
import { TemplateLibraryList } from "./components/TemplateLibraryList";

import { useRbacPrimaryRolePermissions } from "../../api/hooks/defs/rbac";

import { useTemplateLibraryEntries } from "./api/queries";

import { getUserCompanyName } from "../../selectors/app";

import ErrorPage from "../Error";

const TemplateLibraryPage = (): React.ReactElement | null => {
	const { data: permissions, isLoading: permissionsLoading } = useRbacPrimaryRolePermissions("template_library");

	if (permissionsLoading) return null;

	const canView = !!permissions?.includes(TEMPLATE_LIBRARY_PERMISSIONS.viewLibrary);
	const canApply = !!permissions?.includes(TEMPLATE_LIBRARY_PERMISSIONS.applyTemplate);

	if (!canView) return <ErrorPage code={403} />;

	return <TemplateLibraryAuthorizedView canApply={canApply} />;
};

const TemplateLibraryAuthorizedView = ({ canApply }: { canApply: boolean }): React.ReactElement => {
	const [type, setType] = useState<TemplateLibraryEntryTypeEnum | undefined>(undefined);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const companyName = (useSelector(getUserCompanyName) as string | null) ?? "this company";

	// One fetch — counts and the filtered/typed view are derived client-side. The
	// catalog is small (~tens of entries) and global to PerformYard, so client-side
	// filtering avoids a second network round-trip on every chip / search keystroke.
	const { data: allEntries, isLoading } = useTemplateLibraryEntries(undefined);

	const counts = useMemo(() => {
		const next: Record<TemplateLibraryEntryTypeEnum, number> = {
			review_workflow: 0,
			review_form: 0,
			survey: 0,
			pdf_guide: 0,
		};
		(allEntries ?? []).forEach((entry) => {
			next[entry.type] = (next[entry.type] ?? 0) + 1;
		});
		return next;
	}, [allEntries]);

	const filtered = useMemo(() => {
		if (!allEntries) return undefined;
		const typed = type ? allEntries.filter((entry) => entry.type === type) : allEntries;
		const needle = query.trim().toLowerCase();
		if (!needle) return typed;
		return typed.filter(
			(entry) =>
				entry.name.toLowerCase().includes(needle) ||
				entry.description.toLowerCase().includes(needle) ||
				entry.tags.some((tag) => tag.toLowerCase().includes(needle))
		);
	}, [allEntries, type, query]);

	// Resolve selection from visible entries so an empty result cannot show an unrelated preview.
	const visibleSelectedId = filtered?.find((entry) => String(entry._id) === selectedId)?._id ?? filtered?.[0]?._id;
	const activeId = visibleSelectedId ? String(visibleSelectedId) : null;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<section className="ml-12 box-border flex min-h-full flex-col gap-6 bg-neutral-0 px-6 py-4 text-neutral-9 antialiased lg:h-[calc(100dvh-var(--pyo-topbar-height,48px))] lg:min-h-0">
				<PageTitle title="Content Library" />
				<header className="flex shrink-0 flex-col gap-2">
					<Text overrideElement="h1" size="heading-5" weight="semi-bold" color="neutral-9">
						Content Library
					</Text>
					<Text size="paragraph-3" color="neutral-7">
						Explore ready-to-use templates, forms, surveys, and guides. Preview the content, then add a copy
						to your company.
					</Text>
				</header>

				<div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
					<div className="flex flex-col gap-2">
						<Text size="paragraph-3" color="neutral-9">
							Content type
						</Text>
						<TemplateLibraryFilterPills selected={type} onChange={setType} counts={counts} />
					</div>
					<div className="w-full sm:w-[280px]">
						<InputWrapper id="content-library-search" label="Search content library">
							<InputText
								value={query}
								onChange={setQuery}
								placeholder="Search by name, tag, or description"
								aria-label="Search content library"
								leftIcon={faMagnifyingGlass}
								clearable
							/>
						</InputWrapper>
					</div>
				</div>

				<div className="grid grid-cols-1 overflow-hidden rounded-md border border-neutral-3 bg-neutral-0 lg:min-h-0 lg:flex-1 lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)]">
					<div className="min-w-0 lg:relative lg:col-span-5 lg:min-h-0 lg:overflow-y-auto lg:border-r lg:border-neutral-2">
						<TemplateLibraryList
							entries={filtered}
							loading={isLoading}
							selectedId={activeId}
							onSelect={setSelectedId}
							query={query}
						/>
					</div>
					<div className="min-w-0 border-t border-neutral-2 bg-neutral-0 lg:col-span-7 lg:min-h-0 lg:overflow-y-auto lg:border-t-0">
						<TemplateLibraryDetailPanel entryId={activeId} companyName={companyName} canApply={canApply} />
					</div>
				</div>
			</section>
		</Sentry.ErrorBoundary>
	);
};

export default TemplateLibraryPage;
